/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt, Michael Diponio
 * Created: 23/05/22
 */

import * as React from 'react';
import { toColour } from '@/wpt-lib/utils/colourUtils';
import { toTruncatedFixed } from '@/wpt-lib/utils/toTruncatedFixed';
import { toNextMultiple } from '@/wpt-lib/utils/helpers';
import { BarChartData } from '@/wpt-lib/components/Charts/BarChart';
import { wrapToLines } from '@/wpt-lib/utils/chart';
import { G, Line, Rect, Svg, Text, Tspan } from '@react-pdf/renderer';
import {
    checkersPattern,
    circlePattern,
    crossPattern,
    plusPattern,
    wavePattern,
} from '@/wpt-lib/pdf/charts/ChartPatterns';

const BarChart = ({
    data,
    unit = '',
    width,
    height,
    nominalAxisMax = 100,
}: {
    data: BarChartData[];
    unit?: string;
    width: number;
    height: number;
    nominalAxisMax?: number;
}) => {
    const textSpace = 100;
    const textSize = 9;
    const barGapSize = 8;
    const textGap = 4;
    const axisTextGap = 2;
    const chartWidth = width - textSpace - 30;

    const top = textSize;

    const nominalAxisLineCount = 6;
    const nominalAxisLineGap = nominalAxisMax / (nominalAxisLineCount - 1);

    const maxAmount = Math.max(...data.flatMap((v) => [v.amount, v.target]));
    const maxAxisLine = Math.max(nominalAxisMax, toNextMultiple(maxAmount, nominalAxisLineGap));

    const adjustedAxisGapCount = maxAxisLine / nominalAxisLineGap;
    const adjustedAxisLineCount = adjustedAxisGapCount + 1;

    const availableChartHeight = height - textSize;
    const barHeight = Math.min((availableChartHeight - barGapSize * (data.length + 1)) / data.length, 50);
    const chartAreaTop = textSize;

    return (
        <Svg width={width} height={height}>
            <G transform={`translate(${textSpace} 0)`}>
                {[...Array(adjustedAxisLineCount)].map((_, i, arr) => (
                    <G key={i} transform={`translate(${chartWidth * (i / (arr.length - 1))} ${top})`}>
                        <Text x={0} y={0} style={{ fontSize: textSize }} textAnchor="middle">
                            {`${toTruncatedFixed(i * nominalAxisLineGap, 1)}${unit}`}
                        </Text>
                        <Line x1={0} y1={axisTextGap} x2={0} y2="100%" stroke="gray" strokeWidth={0.5} />
                    </G>
                ))}
            </G>
            {data.map(({ amount, target, colourTheme, label }, i) => {
                const color = toColour(colourTheme, 500).name;

                const x = textSpace;
                const y = chartAreaTop + barGapSize * (i + 1) + i * barHeight;

                const labelParts = wrapToLines(label, textSpace, textSize);

                const labelHeight = labelParts.length * textSize + (labelParts.length - 1);
                const labelYPosition = y + barHeight / 2 - labelHeight / 2;

                const widthPattern = Math.ceil((amount / maxAxisLine) * chartWidth);

                const pattern = (patternNumber: number) => {
                    if (patternNumber === 0) {
                        return crossPattern(x, y, widthPattern, barHeight);
                    } else if (patternNumber === 1) {
                        return circlePattern(x, y, widthPattern, barHeight);
                    } else if (patternNumber === 2) {
                        return checkersPattern(x, y, widthPattern, barHeight);
                    } else if (patternNumber === 3) {
                        return plusPattern(x, y, widthPattern, barHeight);
                    } else if (patternNumber === 4) {
                        return wavePattern(x, y, widthPattern, barHeight);
                    }
                };

                return (
                    <G key={i}>
                        <Text
                            style={{
                                fontSize: textSize,
                            }}
                            textAnchor="end"
                            dominantBaseline="text-before-edge"
                            y={labelYPosition}
                        >
                            {labelParts.map((labelPart, labelI) => (
                                <Tspan
                                    key={labelI}
                                    x={x - textGap}
                                    y={labelYPosition + (labelI > 0 ? textSize * labelI + 1 : 0)}
                                >
                                    {labelPart}
                                </Tspan>
                            ))}
                        </Text>
                        <Rect
                            x={x}
                            y={y}
                            height={`${barHeight}px`}
                            width={`${(amount / maxAxisLine) * chartWidth}px`}
                            fill={color}
                        />

                        <Rect
                            x={x}
                            y={y}
                            height={`${barHeight}px`}
                            width={`${(target / maxAxisLine) * chartWidth}px`}
                            fill="none"
                        >
                            {pattern(i % 5)}
                        </Rect>

                        <Rect
                            x={x}
                            y={y}
                            height={barHeight}
                            width={widthPattern}
                            fill="none"
                            strokeWidth={3}
                            stroke="black"
                        />
                        <Text
                            style={{
                                fontSize: 11,
                                fontWeight: 'bold',
                            }}
                        >
                            {labelParts.map((labelPart, labelI) => (
                                <Tspan
                                    key={labelI}
                                    x={Math.min(x + widthPattern + 5, width - 30)}
                                    y={y + barHeight * 0.6}
                                >
                                    {`${amount}%`}
                                </Tspan>
                            ))}
                        </Text>
                    </G>
                );
            })}
        </Svg>
    );
};

export default BarChart;
